import { showApiErrorNotification, useGqtyClient } from '@vrplatform/data';
import { FileDownloadIcon } from '@vrplatform/icons';
import { EllipsisMenuItem } from '@vrplatform/components-legacy';
import {
  showLoadingNotification,
  updateErrorNotification,
  updateSuccessNotification,
  updateWarnNotification,
} from '@vrplatform/theme';
import { useMantineTheme } from '@mantine/core';
import type { source_bool_exp } from '@vrplatform/graphql';
import { type ReactNode, useId, useState } from 'react';

export const EllipsisMenuSourceDownloadItem = ({
  where,
  children,
  notification,
  // fileName,
}: {
  where: source_bool_exp;
  fileName: string;
  children?: ReactNode;
  notification?: string;
}) => {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const $db = useGqtyClient();
  const notifyId = useId();

  // const { mutateAsync } = useApiMutation('get', '/file-storage-link');
  // TODO: Remove the whole file
  const mutateAsync = async (args: any) => {
    throw new Error('Not implemented');
  };

  return (
    <EllipsisMenuItem
      customIcon={
        <FileDownloadIcon size={16} color={theme.colors.neutral[5]} />
      }
      loading={loading}
      onClick={async () => {
        setLoading(true);
        showLoadingNotification({
          id: notifyId,
        });

        const source = await $db
          .query(
            (q) =>
              q
                .source({
                  where,
                  limit: 1,
                  order_by: [
                    {
                      updatedAt: 'desc_nulls_last',
                    },
                  ],
                })
                .map((source) => ({
                  id: source.id,
                  hasFile: !!source
                    .fileStorageLinks({ limit: 1 })
                    .map((x) => x.id)[0],
                }))[0]
          )
          .catch(() => undefined);

        if (!source) {
          updateWarnNotification({
            id: notifyId,
            message: 'No source found',
          });
          setLoading(false);
          return;
        }

        if (!source.hasFile) {
          await $db
            .query(
              (q) =>
                q
                  .source({
                    where: {
                      id: {
                        _eq: source.id,
                      },
                    },
                    limit: 1,
                    order_by: [
                      {
                        updatedAt: 'desc_nulls_last',
                      },
                    ],
                  })
                  .map((source) => (source.json() ?? {}) as object)[0]
            )
            .then((json) => {
              if (!json) {
                updateWarnNotification({
                  id: notifyId,
                  message: 'No source found',
                });

                return;
              }

              // show json in new tab
              const jsonString = JSON.stringify(json, null, 2);
              const blob = new Blob([jsonString], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank', 'noopener,noreferrer');

              updateSuccessNotification({
                id: notifyId,
                message: notification || 'Source was downloaded.',
              });
            })
            .catch((error) => {
              console.error(error);
              updateErrorNotification({
                id: notifyId,
                title: 'Failed to download source',
                message: error.message,
              });
            });
        } else {
          await mutateAsync({
            params: {
              query: {
                sourceId: source.id,
              },
            },
          })
            .then(({ fileUrl }) => {
              window.open(fileUrl, '_blank', 'noopener,noreferrer');
              updateSuccessNotification({
                id: notifyId,
                title: 'Success!',
                message: notification || 'Source downloaded.',
              });
            })
            .catch((error) => {
              console.error(error);
              showApiErrorNotification({
                id: notifyId,
                title: 'Error',
                error,
                defaultMessage: 'Failed to download source',
              });
            });
        }

        setLoading(false);
      }}
    >
      {children || 'Download source'}
    </EllipsisMenuItem>
  );
};
